import React from 'react';
import {
 Input
} from 'shared/components/ui/input';
import {
 Label
} from 'shared/components/ui/label';
import {
 Checkbox
} from 'shared/components/ui/checkbox';
import {
 Tooltip, TooltipProvider, TooltipTrigger, TooltipContent
} from 'shared/components/ui/tooltip';
import {
 Textarea
} from 'shared/components/ui/textarea';
import {
 Info
} from 'lucide-react';

interface ConnectionSettings {
    hosts?: string[];
    replica_set?: string;
    auth_source?: string;
    host?: string;
    port?: number;
    db?: number;
    max_connections?: number;
    username?: string;
    password?: string;
    connection_timeout?: number;
    tls?: boolean;
    ssl?: boolean;
}

interface NoSqlDbFieldsProps {
    type: 'mongodb' | 'redis';
    connection: { settings?: ConnectionSettings };
}

const inputStyles = 'focus-visible:ring-neutral-950 dark:focus-visible:ring-neutral-300';
const checkboxStyles =
    'data-[state=checked]:bg-neutral-950 data-[state=checked]:text-neutral-50 dark:data-[state=checked]:bg-neutral-50 dark:data-[state=checked]:text-neutral-950';

const tooltipIconStyles = 'inline-block w-4 h-4 text-gray-500 ml-2 cursor-pointer hover:text-gray-800';

// Tooltips content
const tooltips = {
    hosts: 'Provide a list of MongoDB replica set hosts, separated by commas.',
    replica_set: 'Optional. If you have a replica set, provide its name.',
    auth_source: "The authentication database to use. Default is 'admin'.",
    host: "The address of the Redis server (e.g., 'localhost').",
    port: 'The port to connect to. Default is 6379.',
    db: 'The Redis database index (0–15). Default is 0.',
    username: 'Optional. The username for Redis ACL (if any).',
    password: 'Optional. The password for Redis or MongoDB.',
    connection_timeout: 'The time to wait before timing out the connection, in seconds.',
    max_connections: 'Maximum number of simultaneous connections to the Redis server.',
    tls: 'Enable TLS/SSL encryption for secure connections.',
};

export const NoSqlDbFields: React.FC<NoSqlDbFieldsProps> = ({
 type, connection
}) => {
    const settings = connection?.settings || {};

    return (
        <TooltipProvider>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                {type === 'mongodb' && (
                    <>
                        <div className="space-y-2 md:col-span-2">
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <Label htmlFor="hosts" className="select-none text-xs">
                                        Hosts (comma separated)
                                        <Info className={tooltipIconStyles} />
                                    </Label>
                                </TooltipTrigger>
                                <TooltipContent className="w-[300px]">
                                    <p>{tooltips.hosts}</p>
                                </TooltipContent>
                            </Tooltip>
                            <Textarea
                                id="hosts"
                                name="hosts"
                                required
                                placeholder="host1.mongodb.net,host2.mongodb.net"
                                defaultValue={(settings.hosts || []).join(',')}
                                className={inputStyles}
                            />
                        </div>

                        <div className="space-y-2">
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <Label htmlFor="replica_set" className="select-none text-xs">
                                        Replica Set (Optional)
                                        <Info className={tooltipIconStyles} />
                                    </Label>
                                </TooltipTrigger>
                                <TooltipContent className="w-[300px]">
                                    <p>{tooltips.replica_set}</p>
                                </TooltipContent>
                            </Tooltip>
                            <Input
                                id="replica_set"
                                name="replica_set"
                                placeholder="e.g., rs0"
                                defaultValue={settings.replica_set}
                                className={inputStyles}
                            />
                        </div>

                        <div className="space-y-2">
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <Label htmlFor="auth_source" className="select-none text-xs">
                                        Auth Source
                                        <Info className={tooltipIconStyles} />
                                    </Label>
                                </TooltipTrigger>
                                <TooltipContent className="w-[300px]">
                                    <p>{tooltips.auth_source}</p>
                                </TooltipContent>
                            </Tooltip>
                            <Input
                                id="auth_source"
                                name="auth_source"
                                defaultValue={settings.auth_source || 'admin'}
                                className={inputStyles}
                            />
                        </div>
                    </>
                )}

                {type === 'redis' && (
                    <>
                        <div className="space-y-2">
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <Label htmlFor="host" className="select-none text-xs">
                                        Host
                                        <Info className={tooltipIconStyles} />
                                    </Label>
                                </TooltipTrigger>
                                <TooltipContent className="w-[300px]">
                                    <p>{tooltips.host}</p>
                                </TooltipContent>
                            </Tooltip>
                            <Input
                                id="host"
                                name="host"
                                required
                                placeholder="localhost"
                                defaultValue={settings.host}
                                className={inputStyles}
                            />
                        </div>

                        <div className="space-y-2">
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <Label htmlFor="port" className="select-none text-xs">
                                        Port
                                        <Info className={tooltipIconStyles} />
                                    </Label>
                                </TooltipTrigger>
                                <TooltipContent className="w-[300px]">
                                    <p>{tooltips.port}</p>
                                </TooltipContent>
                            </Tooltip>
                            <Input
                                id="port"
                                name="port"
                                type="number"
                                required
                                placeholder="6379"
                                defaultValue={settings.port || 6379}
                                className={inputStyles}
                            />
                        </div>

                        <div className="space-y-2">
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <Label htmlFor="db" className="select-none text-xs">
                                        Database Index
                                        <Info className={tooltipIconStyles} />
                                    </Label>
                                </TooltipTrigger>
                                <TooltipContent className="w-[300px]">
                                    <p>{tooltips.db}</p>
                                </TooltipContent>
                            </Tooltip>
                            <Input
                                id="db"
                                name="db"
                                type="number"
                                min={0}
                                max={15}
                                defaultValue={settings.db || 0}
                                className={inputStyles}
                            />
                        </div>

                    </>
                )}

                {/* Common optional fields */}
                <div className="space-y-2">
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <Label htmlFor="username" className="select-none text-xs">
                                Username (Optional)
                                <Info className={tooltipIconStyles} />
                            </Label>
                        </TooltipTrigger>
                        <TooltipContent className="w-[300px]">
                            <p>{tooltips.username}</p>
                        </TooltipContent>
                    </Tooltip>
                    <Input
                        id="username"
                        name="username"
                        placeholder="e.g., default"
                        defaultValue={settings.username}
                        className={inputStyles}
                    />
                </div>

                <div className="space-y-2">
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <Label htmlFor="password" className="select-none text-xs">
                                Password (Optional)
                                <Info className={tooltipIconStyles} />
                            </Label>
                        </TooltipTrigger>
                        <TooltipContent className="w-[300px]">
                            <p>{tooltips.password}</p>
                        </TooltipContent>
                    </Tooltip>
                    <Input
                        id="password"
                        name="password"
                        type="password"
                        defaultValue={settings.password}
                        className={inputStyles}
                    />
                </div>

                <div className="space-y-2">
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <Label htmlFor="connection_timeout" className="select-none text-xs">
                                Connection Timeout (seconds)
                                <Info className={tooltipIconStyles} />
                            </Label>
                        </TooltipTrigger>
                        <TooltipContent className="w-[300px]">
                            <p>{tooltips.connection_timeout}</p>
                        </TooltipContent>
                    </Tooltip>
                    <Input
                        id="connection_timeout"
                        name="connection_timeout"
                        type="number"
                        defaultValue={settings.connection_timeout || 30}
                        className={inputStyles}
                    />
                </div>

                <div className="flex items-center space-x-2 pt-6 md:col-span-2">
                    <Checkbox
                        id={type === 'mongodb' ? 'tls' : 'ssl'}
                        name={type === 'mongodb' ? 'tls' : 'ssl'}
                        className={checkboxStyles}
                        defaultChecked={Boolean(type === 'mongodb' ? settings.tls : settings.ssl)}
                    />
                    <Label htmlFor={type === 'mongodb' ? 'tls' : 'ssl'} className="font-normal">
                        Enable {type === 'mongodb' ? 'TLS' : 'SSL'} Encryption
                    </Label>
                </div>
            </div>
        </TooltipProvider>
    );
};
